import React, { useState, useCallback, useRef, Component, ErrorInfo, useEffect } from 'react';
import { View, Text, Platform, Pressable, ScrollView, Alert, TextInput } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { MaterialCommunityIcons, AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { getComponentById, getAllComponents } from '@registry/componentRegistry';
import apis from '@api/api';
import { useDispatch } from 'react-redux';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { useQuery } from '@tanstack/react-query';

// Error boundary to catch infinite loops in chart components
class ComponentErrorBoundary extends Component<
  { componentId: string; children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { componentId: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`❌ Error rendering component ${this.props.componentId}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={{ padding: 20, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: '#ef4444', fontSize: 12, textAlign: 'center' }}>
            Error rendering {this.props.componentId}
          </Text>
          <Text style={{ color: '#9ca3af', fontSize: 10, marginTop: 8, textAlign: 'center' }}>
            {this.state.error?.message || 'Component caused an error'}
          </Text>
        </View>
      );
    }

    return this.props.children;
  }
}

interface DashboardComponent {
  componentId: string;
  componentType: string;
  config: any;
  dataMapping: any;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
}

interface DashboardBuilderProps {
  dashboardConfig: {
    dataSourceIds: string[];
    components: Array<{
      componentId: string;
      componentType: string;
      config: any;
      confidence?: number;
      reasoning?: string;
    }>;
    layout?: any;
  };
  theme: boolean;
  onSave?: (dashboardId: string) => void;
}

export const DashboardBuilder: React.FC<DashboardBuilderProps> = ({
  dashboardConfig,
  theme,
  onSave
}) => {
  const dispatch = useDispatch();
  const [components, setComponents] = useState<DashboardComponent[]>(() => {
    const initialComponents = dashboardConfig.components.map((comp, index) => {
      const component = {
        componentId: comp.componentId,
        componentType: comp.componentType,
        config: comp.config || {},
        // Preserve dataMapping from backend (includes dataSourceId, columns, seriesData, data, etc.)
        dataMapping: comp.dataMapping || {},
        position: { x: (index % 2) * 50, y: Math.floor(index / 2) * 200 },
        size: { width: 48, height: 200 }
      };
      
      // Debug: Log initial component data
      if (__DEV__) {
        console.log(`📊 [DashboardBuilder] Initializing ${comp.componentId}:`, {
          hasData: Array.isArray(comp.dataMapping?.data) && comp.dataMapping.data.length > 0,
          dataLength: comp.dataMapping?.data?.length || 0,
          hasColumns: Array.isArray(comp.dataMapping?.columns) && comp.dataMapping.columns.length > 0,
          columnsLength: comp.dataMapping?.columns?.length || 0,
        });
      }
      
      return component;
    });
    
    return initialComponents;
  });
  const [dashboardTitle, setDashboardTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [draggedComponent, setDraggedComponent] = useState<string | null>(null);
  const [dataSourceData, setDataSourceData] = useState<Record<string, any>>({});

  // Helper function to generate series data from fetched data
  const generateSeriesDataFromFetchedData = (data: any[], columns: any[]): Array<{ x: string; y: number }> => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    if (!columns || !Array.isArray(columns) || columns.length === 0) return [];

    // Find numeric columns
    const numericColumns = columns.filter((col: any) => {
      const accessor = col.accessor || col.key || col.id;
      if (!accessor) return false;
      
      // Check if first data item has this field and it's numeric
      const firstValue = data[0]?.[accessor];
      return typeof firstValue === 'number';
    });

    if (numericColumns.length === 0) return [];

    // Use first numeric column for y-axis
    const yAccessor = numericColumns[0].accessor || numericColumns[0].key || numericColumns[0].id;
    
    // Try to find a label/category column (string type)
    const labelColumns = columns.filter((col: any) => {
      const accessor = col.accessor || col.key || col.id;
      if (!accessor || accessor === yAccessor) return false;
      const firstValue = data[0]?.[accessor];
      return typeof firstValue === 'string';
    });
    const xAccessor = labelColumns[0]?.accessor || labelColumns[0]?.key || labelColumns[0]?.id;

    // Generate series data
    return data.map((item, index) => ({
      x: xAccessor ? (item[xAccessor] || `Item ${index + 1}`) : `Item ${index + 1}`,
      y: item[yAccessor] || 0
    }));
  };

  // Fetch data from all data sources (only if not already provided in suggest-components response)
  useEffect(() => {
    const fetchDataSourceData = async () => {
      if (!dashboardConfig.dataSourceIds || dashboardConfig.dataSourceIds.length === 0) {
        return;
      }

      // Check if components already have data from suggest-components (check initial config, not state)
      const hasExistingData = dashboardConfig.components.some((comp: any) => {
        const hasData = Array.isArray(comp.dataMapping?.data) && comp.dataMapping.data.length > 0;
        const hasSeriesData = Array.isArray(comp.dataMapping?.seriesData) && comp.dataMapping.seriesData.length > 0;
        return hasData || hasSeriesData;
      });

      // If we already have data, use it immediately and fetch fresh data in background
      if (hasExistingData) {
        console.log('📊 [DashboardBuilder] Using existing data from suggest-components, fetching fresh data in background');
        // Components are already initialized with data from dashboardConfig, so they should render immediately
        // Just ensure all components have proper structure
        setComponents(prevComponents => 
          prevComponents.map(comp => {
            // Ensure table components have proper structure
            if (comp.componentId === 'table.data') {
              return {
                ...comp,
                dataMapping: {
                  ...comp.dataMapping,
                  data: comp.dataMapping?.data || [],
                  columns: comp.dataMapping?.columns || comp.config?.columns || [],
                  dataSchema: comp.dataMapping?.dataSchema || [],
                },
                config: {
                  ...comp.config,
                  columns: comp.dataMapping?.columns || comp.config?.columns || [],
                }
              };
            }
            // Ensure list components have data
            if (comp.componentId === 'list.items') {
              return {
                ...comp,
                dataMapping: {
                  ...comp.dataMapping,
                  data: comp.dataMapping?.data || [],
                }
              };
            }
            return comp;
          })
        );
      } else {
        dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Loading data...' } });
      }
      
      try {
        const dataPromises = dashboardConfig.dataSourceIds.map(async (dataSourceId: string) => {
          try {
            // getDataContent takes (dataId, type) - use 'all' as default type
            const response = await apis.getDataContent(dataSourceId, 'all');
            // Handle both response formats: { data: { dataContent: [...] } } and { dataContent: [...] }
            const dataItems = response?.data?.dataContent || response?.dataContent || [];
            
            // Extract actual data from DataItems - each item has a nested 'data' property
            const extractedData = dataItems.map((item: any) => {
              // DataItems have structure: { _id, dataId, data: { actual fields }, ... }
              if (item.data && typeof item.data === 'object' && !Array.isArray(item.data)) {
                // Return the nested data object with metadata
                return {
                  ...item.data,
                  _id: item._id,
                  dataItemId: item._id,
                };
              }
              // If no nested data, return the item as-is
              return item;
            });
            
            return { id: dataSourceId, data: extractedData };
          } catch (error) {
            console.error(`Error fetching data for source ${dataSourceId}:`, error);
            return { id: dataSourceId, data: [] };
          }
        });

        const results = await Promise.all(dataPromises);
        const dataMap: Record<string, any> = {};
        results.forEach(({ id, data }) => {
          dataMap[id] = data;
        });

        setDataSourceData(dataMap);
        if (!hasExistingData) {
          dispatch({ type: HIDE_LOADING_MODAL });
        }

        // Update components with fetched data (this will refresh with latest data)
        setComponents(prevComponents => 
          prevComponents.map(comp => {
            // Use the dataSourceId from the component's dataMapping (set by backend)
            const dataSourceId = comp.dataMapping?.dataSourceId || dashboardConfig.dataSourceIds[0];
            const fetchedData = dataMap[dataSourceId] || [];
            
            // Merge fetched data with existing dataMapping and config
            // This preserves columns, seriesData, etc. from backend while adding actual data
            if (comp.componentId === 'table.data') {
              // For tables: prefer fetched data (fresh from API), but fallback to existing data from suggest-components
              // This ensures we always have the latest data, but can still show data if fetch fails
              const tableData = Array.isArray(fetchedData) && fetchedData.length > 0 
                ? fetchedData 
                : (Array.isArray(comp.dataMapping?.data) && comp.dataMapping.data.length > 0
                    ? comp.dataMapping.data 
                    : []);
              
              console.log(`📊 [DashboardBuilder] Table component data updated:`, {
                componentId: comp.componentId,
                fetchedDataLength: fetchedData.length,
                existingDataLength: comp.dataMapping?.data?.length || 0,
                finalDataLength: tableData.length,
                hasColumns: (comp.dataMapping?.columns || comp.config?.columns || []).length > 0,
              });
              
              return {
                ...comp,
                dataMapping: {
                  ...comp.dataMapping,
                  data: tableData,
                  dataSourceId: dataSourceId,
                  // Preserve columns from backend config if available
                  columns: comp.dataMapping?.columns || comp.config?.columns || [],
                  // Preserve dataSchema if available
                  dataSchema: comp.dataMapping?.dataSchema || [],
                },
                // Ensure config has columns too
                config: {
                  ...comp.config,
                  columns: comp.dataMapping?.columns || comp.config?.columns || [],
                }
              };
            } else if (comp.componentId === 'chart.bar') {
              // For charts: use seriesData from backend config if available
              // Otherwise try to generate from fetched data
              let seriesData = comp.dataMapping?.seriesData || comp.config?.seriesData || [];
              
              // If we have fetched data but no seriesData, try to generate it
              if (Array.isArray(fetchedData) && fetchedData.length > 0 && seriesData.length === 0) {
                // Try to infer columns from data structure
                const firstItem = fetchedData[0];
                if (firstItem && typeof firstItem === 'object') {
                  const keys = Object.keys(firstItem);
                  const numericKeys = keys.filter(key => typeof firstItem[key] === 'number');
                  const stringKeys = keys.filter(key => typeof firstItem[key] === 'string' && firstItem[key]);
                  
                  if (numericKeys.length > 0) {
                    // Use numeric field for y-axis
                    const yKey = numericKeys[0];
                    const xKey = stringKeys[0] || numericKeys[0];
                    seriesData = fetchedData.map((item, idx) => ({
                      x: item[xKey] || `Item ${idx + 1}`,
                      y: item[yKey] || 0
                    }));
                  } else if (stringKeys.length > 0) {
                    // No numeric data - create count-based visualization
                    // Group by first string field and count occurrences
                    const groupKey = stringKeys[0];
                    const grouped: Record<string, number> = {};
                    
                    fetchedData.forEach((item) => {
                      const value = item[groupKey] || 'Unknown';
                      grouped[value] = (grouped[value] || 0) + 1;
                    });
                    
                    seriesData = Object.entries(grouped)
                      .map(([x, y]) => ({ x, y }))
                      .slice(0, 10); // Limit to top 10
                    
                    console.log(`📊 [DashboardBuilder] Generated count-based chart data for ${comp.componentId}:`, {
                      groupKey,
                      seriesDataLength: seriesData.length,
                    });
                  }
                }
              }
              
              return {
                ...comp,
                dataMapping: {
                  ...comp.dataMapping,
                  seriesData: seriesData,
                  dataSourceId: dataSourceId,
                  seriesName: comp.dataMapping?.seriesName || comp.config?.seriesName || 'Data',
                },
                config: {
                  ...comp.config,
                  seriesData: seriesData,
                  seriesName: comp.dataMapping?.seriesName || comp.config?.seriesName || 'Data',
                }
              };
            } else {
              // For other component types, just add the data
              return {
                ...comp,
                dataMapping: {
                  ...comp.dataMapping,
                  data: Array.isArray(fetchedData) ? fetchedData : [],
                  dataSourceId: dataSourceId,
                }
              };
            }
          })
        );
      } catch (error) {
        console.error('Error fetching data sources:', error);
        dispatch({ type: HIDE_LOADING_MODAL });
      }
    };

    fetchDataSourceData();
  }, [dashboardConfig.dataSourceIds, dashboardConfig.components, dispatch]);

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    border: theme ? '#e5e7eb' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    accent: '#3b82f6',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
  };

  const renderComponent = useCallback((component: DashboardComponent, index: number) => {
    const componentEntry = getComponentById(component.componentId);
    
    if (!componentEntry) {
      console.warn(`Component ${component.componentId} not found in registry`);
      return (
        <View
          key={index}
          style={{
            backgroundColor: cardColors.surface,
            borderRadius: 8,
            padding: 16,
            margin: 8,
            borderWidth: 1,
            borderColor: cardColors.border,
            minHeight: 200,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text style={{ color: cardColors.textSecondary, fontSize: 12 }}>
            Component {component.componentId} not available
          </Text>
        </View>
      );
    }

    const Component = componentEntry.component;
    
    // Prepare component data - merge config and dataMapping
    // For list.items, pass data directly; for others, pass merged object
    let componentData: any;
    if (component.componentId === 'list.items') {
      // ItemsList expects data prop with array or { data: array }
      componentData = {
        data: component.dataMapping?.data || component.config?.data || [],
      };
    } else if (component.componentId === 'chart.bar') {
      // BarChart expects data prop with seriesData
      componentData = {
        seriesData: component.dataMapping?.seriesData || component.config?.seriesData || [],
        seriesName: component.dataMapping?.seriesName || component.config?.seriesName || 'Data',
        categories: component.dataMapping?.categories || component.config?.categories || [],
        height: component.dataMapping?.height || component.config?.height || 310,
        colors: component.dataMapping?.colors || component.config?.colors || ['#487FFF'],
      };
    } else {
      // For other components, merge config and dataMapping
      componentData = {
        ...component.config,
        ...component.dataMapping,
      };
    }
    
    // Debug logging
    if (__DEV__) {
      console.log(`📊 [DashboardBuilder] Rendering ${component.componentId}:`, {
        componentType: component.componentType,
        hasConfig: Object.keys(component.config || {}).length > 0,
        hasDataMapping: Object.keys(component.dataMapping || {}).length > 0,
        dataKeys: Object.keys(componentData),
        dataLength: componentData.data?.length || componentData.seriesData?.length || 0,
        columnsLength: componentData.columns?.length || 0,
        dataSample: componentData.data?.slice(0, 2) || componentData.seriesData?.slice(0, 2) || [],
        columnsSample: componentData.columns?.slice(0, 3) || [],
      });
    }

    // Wrap component in error boundary to catch infinite loops
    // Use React.memo pattern to prevent unnecessary re-renders
    const MemoizedComponent = React.memo(Component);

    return (
      <View
        key={`${component.componentId}-${index}`}
        style={{
          backgroundColor: cardColors.surface,
          borderRadius: 8,
          padding: 12,
          margin: 8,
          borderWidth: 1,
          borderColor: draggedComponent === component.componentId ? cardColors.accent : cardColors.border,
          minHeight: component.size?.height || 200,
          width: component.size?.width ? `${component.size.width}%` : '48%',
          position: 'relative',
        }}
      >
        {isEditing && (
          <Pressable
            onPress={() => {
              setComponents(prev => prev.filter((_, i) => i !== index));
            }}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: '#ef4444',
              borderRadius: 12,
              width: 24,
              height: 24,
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            <AntDesign name="close" size={14} color="white" />
          </Pressable>
        )}
        
        {/* Render component with error boundary to catch infinite loops */}
        <ComponentErrorBoundary componentId={component.componentId}>
          <Component data={componentData} />
        </ComponentErrorBoundary>
      </View>
    );
  }, [theme, cardColors, isEditing, draggedComponent]);

  const handleSave = useCallback(async () => {
    if (!dashboardTitle.trim()) {
      Alert.alert('Title Required', 'Please enter a title for your dashboard');
      return;
    }

    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Saving dashboard...' } });

      const dashboardData = {
        title: dashboardTitle,
        description: `Dashboard with ${components.length} components`,
        dataSourceIds: dashboardConfig.dataSourceIds,
        layout: {
          type: 'grid',
          columns: 2,
        },
        components: components.map(comp => ({
          componentId: comp.componentId,
          componentType: comp.componentType,
          config: comp.config,
          dataMapping: comp.dataMapping,
          position: comp.position,
          size: comp.size,
        })),
      };

      const response = await apis.createDashboard(dashboardData);
      
      dispatch({ type: HIDE_LOADING_MODAL });

      if (response?.success) {
        dispatch({ 
          type: SHOW_AUTH_MODAL, 
          data: { content: 'Dashboard saved successfully!', type: 'success' } 
        });
        onSave?.(response.data?.dashboardId || response.data?._id);
      } else {
        throw new Error(response?.error || 'Failed to save dashboard');
      }
    } catch (error: any) {
      console.error('Error saving dashboard:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ 
        type: SHOW_AUTH_MODAL, 
        data: { content: `Failed to save dashboard: ${error.message}`, type: 'error' } 
      });
    }
  }, [dashboardTitle, components, dashboardConfig, dispatch, onSave]);

  return (
    <View
      style={{
        backgroundColor: cardColors.background,
        borderRadius: 12,
        padding: 16,
        marginVertical: 12,
        borderWidth: 1,
        borderColor: cardColors.border,
      }}
    >
      <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Text style={{ fontSize: 18, fontWeight: '600', color: cardColors.text }}>
          Dashboard Preview
        </Text>
        <Pressable
          onPress={() => setIsEditing(!isEditing)}
          style={{
            paddingHorizontal: 12,
            paddingVertical: 6,
            backgroundColor: isEditing ? cardColors.accent : cardColors.surface,
            borderRadius: 6,
          }}
        >
          <Text style={{ color: isEditing ? 'white' : cardColors.text, fontSize: 12, fontWeight: '600' }}>
            {isEditing ? 'Done Editing' : 'Edit Layout'}
          </Text>
        </Pressable>
      </XStack>

      {isEditing && (
        <View style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 14, color: cardColors.text, marginBottom: 8 }}>
            Dashboard Title
          </Text>
          <TextInput
            value={dashboardTitle}
            onChangeText={setDashboardTitle}
            placeholder="Enter dashboard title..."
            placeholderTextColor={cardColors.textSecondary}
            style={{
              backgroundColor: cardColors.surface,
              borderRadius: 8,
              padding: 12,
              borderWidth: 1,
              borderColor: cardColors.border,
              color: cardColors.text,
              fontSize: 14,
            }}
          />
        </View>
      )}

      <ScrollView
        style={{ maxHeight: 600 }}
        contentContainerStyle={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
      >
        {components.map((component, index) => renderComponent(component, index))}
      </ScrollView>

      <XStack gap={12} marginTop={16} justifyContent="flex-end">
        <Pressable
          onPress={() => setIsEditing(false)}
          style={{
            paddingHorizontal: 16,
            paddingVertical: 10,
            backgroundColor: cardColors.surface,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: cardColors.border,
          }}
        >
          <Text style={{ color: cardColors.text, fontSize: 14, fontWeight: '600' }}>
            Cancel
          </Text>
        </Pressable>
        <Pressable
          onPress={handleSave}
          disabled={!dashboardTitle.trim()}
          style={{
            paddingHorizontal: 16,
            paddingVertical: 10,
            backgroundColor: dashboardTitle.trim() ? cardColors.accent : cardColors.textSecondary,
            borderRadius: 8,
            opacity: dashboardTitle.trim() ? 1 : 0.5,
          }}
        >
          <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
            Save Dashboard
          </Text>
        </Pressable>
      </XStack>
    </View>
  );
};

export default DashboardBuilder;

